import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { t } from "components/CentralDataMangement/translation/Translation";
import Button from "components/common/atoms/Button";
import InputTextfield from "components/common/atoms/InputTextField";
import AddIcon from "static/images/AddIcon";
import FilterClose from "static/images/FilterClose";
import SendIcon from "static/images/SendIcon";
import type { PreviewModalProps } from "../annotations/WhatsAppAnnotations";
import {
  formatBytes,
  getExtensionFromMediaType,
  renderFileIcon,
} from "./helpers";

const PreviewModal: React.FC<PreviewModalProps> = ({
  files,
  handlePreviewClose,
  handleRemoveFile,
  handleSendMessage,
  handleChange,
  handleFileUpload,
  isMessageSending,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedImageIndex, setSelectedImageIndex] = useState(
    files.media.length - 1
  );

  const prevMediaLengthRef = useRef<number>(files.media.length);

  useEffect(() => {
    const prevLength = prevMediaLengthRef.current;
    const currentLength = files.media.length;

    if (currentLength > prevLength) {
      setSelectedImageIndex(currentLength - 1);
    } else if (currentLength < prevLength) {
      if (selectedImageIndex >= currentLength) {
        setSelectedImageIndex(Math.max(currentLength - 1, 0));
      }
    }

    prevMediaLengthRef.current = currentLength;
  }, [files.media, selectedImageIndex]);

  const handleClick = useCallback(() => {
    let acceptTypes = "";
    const lastMedia = files.media?.[files.media.length - 1];

    if (lastMedia?.mediaType?.startsWith("image/")) {
      acceptTypes = ".jpg,.jpeg,.png";
    } else {
      acceptTypes = ".pdf,.doc,.docx,.txt, .xls, .xlsx";
    }

    if (fileInputRef.current) {
      fileInputRef.current.accept = acceptTypes;
      fileInputRef.current.click();
    }
  }, [files.media]);

  const handleSelectedImage = useCallback((index: number) => {
    setSelectedImageIndex(index);
  }, []);

  const selectedFile = useMemo(() => {
    return files.media?.[selectedImageIndex] || null;
  }, [files.media, selectedImageIndex]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !isMessageSending) {
      e.preventDefault(); // Optional: prevents line break in multi-line inputs
      handleSendMessage();
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        {/* Header */}
        <div className="modal-header d-flex justify-content-between align-items-center">
          <strong>{t("Preview")}</strong>
          <Button
            className="whatsapp-icons whatsapp-close-icon border-0 bg-transparent"
            handleClick={handlePreviewClose}
          >
            <FilterClose />
          </Button>
        </div>

        {/* Preview Section */}
        {selectedFile && (
          <div className="modal-body text-center d-flex align-items-center justify-content-center">
            {(() => {
              const { mediaType, mediaFile, mediaName, mediaSize } =
                selectedFile;

              if (!mediaFile) {
                return <div>No file available to preview.</div>;
              }
              switch (true) {
                case mediaType.startsWith("image/"):
                  return (
                    <img
                      src={URL.createObjectURL(mediaFile)}
                      alt="Selected Preview"
                      className="img-fluid whatsapp-image"
                    />
                  );
                case mediaType.startsWith("application/") ||
                  mediaType.startsWith("text/"):
                  const IconComponent = renderFileIcon(mediaType);
                  return (
                    <div className="d-flex flex-column align-items-center justify-content-center document-wrapper">
                      <IconComponent />
                      <span className="file-name-preview marginTop1">
                        {mediaName}
                        <div>
                           {formatBytes(mediaSize)}
                        </div>
                      </span>
                    </div>
                  );
                default:
                  const DefaultIcon = renderFileIcon("");
                  return (
                    <div className="d-flex flex-column align-items-center justify-content-center document-wrapper">
                      <DefaultIcon />
                      <span className="file-name-preview marginTop1">
                        {mediaName}
                      </span>
                    </div>
                  );
              }
            })()}
          </div>
        )}

        {/* Caption Input */}
        {selectedFile && (
          <div className="row hideHeight">
            <div className="col-7 mx-auto marginBottom2">
              <InputTextfield
                placeholder={t("Add a caption")}
                name="caption"
                value={selectedFile.mediaCaption}
                handleChange={(e: any) =>
                  handleChange(e, "media", selectedImageIndex)
                }
                onKeyDown={handleKeyDown}
              />
            </div>
          </div>
        )}

        {/* Thumbnails */}
        <div className="row">
          <div className="col-8 mx-auto d-flex flex-wrap justify-content-center gap-2">
            {files.media.map((file: any, index: number) => {
              const isSelected = selectedImageIndex === index;
              const mediaExt = getExtensionFromMediaType(file.mediaType);
              return (
                <div
                  key={index}
                  className={`position-relative whatsapp-thumbnail ${
                    isSelected ? "selected-border" : "thumbnail-image-border"
                  } ${mediaExt && "document-thumbnail"} img-box`}
                  onClick={() => handleSelectedImage(index)}
                >
                  {(() => {
                    const { mediaType, mediaFile, mediaName } = file;

                    if (!mediaFile) {
                      return <div>No file available to preview.</div>;
                    }
                    switch (true) {
                      case mediaType.startsWith("image/"):
                        return (
                          <span title={mediaName}>
                            <img
                              src={URL.createObjectURL(mediaFile)}
                              alt={`Thumbnail ${index}`}
                              className="img-fluid thumbnail-image"
                            />
                          </span>
                        );
                      case mediaType.startsWith("application/") ||
                        mediaType.startsWith("text/"):
                        const IconComponent = renderFileIcon(mediaType);
                        return (
                          <span
                            title={mediaName}
                            className="d-flex align-items-center justify-content-center w-100 h-100"
                          >
                            <IconComponent />
                          </span>
                        );
                      default:
                        const DefaultIcon = renderFileIcon("");
                        return (
                          <span title={mediaName}>
                            <DefaultIcon />
                          </span>
                        );
                    }
                  })()}
                  <span
                    className="preview-close-icon"
                    onClick={(e) => {
                      handleRemoveFile(index);
                    }}
                  >
                    <FilterClose />
                  </span>
                </div>
              );
            })}

            {/* Add New */}
            {files.media.length < 5 && (
              <div
                className="img-box whatsapp-add-icon d-flex align-items-center justify-content-center cursor-pointer"
                onClick={handleClick}
              >
                <AddIcon />
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer d-flex justify-content-end">
          <Button
            handleClick={handleSendMessage}
            className="whatsapp-icons border-0 bg-transparent"
            disabled={isMessageSending}
          >
            <SendIcon />
          </Button>
        </div>

        {/* Hidden File Input */}
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileUpload}
          multiple
        />
      </div>
    </div>
  );
};

export default PreviewModal;
